import React from 'react';
import {
  ArrowForward as ArrowForwardIcon,
  CreditCard as CreditCardIcon,
  Savings as SavingsIcon,
  AccountBalance as CheckingIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

type AccountSurface = {
  title: string;
  description: string;
  route: string;
  cta: string;
  icon: React.ReactNode;
};

const accountSurfaces: AccountSurface[] = [
  {
    title: 'Checking',
    description: 'Manage day-to-day cash accounts used for bills, spending, and transfers.',
    route: '/accounts/checking',
    cta: 'Open Checking',
    icon: <CheckingIcon />,
  },
  {
    title: 'Savings',
    description: 'Track reserves and long-term buckets while keeping household visibility clear.',
    route: '/accounts/savings',
    cta: 'Open Savings',
    icon: <SavingsIcon />,
  },
  {
    title: 'Credit Cards',
    description: 'Review card balances, limits, and statement-cycle settings in one place.',
    route: '/accounts/credit-cards',
    cta: 'Open Credit Cards',
    icon: <CreditCardIcon />,
  },
];

const AccountsPage: React.FC = () => (
  <Stack spacing={3}>
    <Paper
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 2,
        background:
          'linear-gradient(135deg, rgba(39,174,96,0.14) 0%, rgba(46,125,50,0.06) 55%, rgba(255,255,255,0.95) 100%)',
      }}
    >
      <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
        Accounts
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
        Choose an account area to review and manage household finances by type.
      </Typography>
    </Paper>

    <Grid container spacing={2}>
      {accountSurfaces.map((surface) => (
        <Grid size={{ xs: 12, md: 4 }} key={surface.route}>
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '50%',
                  color: 'primary.main',
                  backgroundColor: 'rgba(46,125,50,0.12)',
                }}
              >
                {surface.icon}
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {surface.title}
              </Typography>
            </Stack>

            <Typography color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
              {surface.description}
            </Typography>

            <Button
              component={RouterLink}
              to={surface.route}
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {surface.cta}
            </Button>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Stack>
);

export default AccountsPage;
